/**
 * Which cycles a DAY actually runs in, and what a gesture on one of those cycles means.
 *
 * `cycleWindow.ts` answers the question one level up — the span a day's PHASE covers, which is the
 * frame everything here resolves inside. This module is the day's own answer inside that frame:
 * `activationCycle`, `retirementCycle` and `excludedCycles` resolved into a concrete list, and the
 * two gestures the calendar authors expressed as decisions rather than as field writes.
 *
 * ── Why the decisions are here and the writes are in `draftOperations` ───────────────────────
 * "Remove this training from this week" has four possible meanings depending on the day's current
 * window, and exactly one of them is a field patch. Excluding the only cycle a day still runs in is
 * a DELETION, and sending it as a hole earns a 400 (`plan_window_empty`, `PlanWindowPolicy`
 * `EnsureNoWindowIsEmpty`); retiring a day from before its own first cycle is the same deletion
 * written the other way. Deciding that in a component, or inside a mutator that has already started
 * writing, is how one of the four ends up unhandled — so the decision is a pure function that
 * returns which operation to run, and the operations do one thing each.
 *
 * ── The coordinates are PLAN-ABSOLUTE and they resolve inside the phase ──────────────────────
 * Every cycle number here is 1-based across the whole plan, the same axis `ProgressionOverride`
 * uses. The window is clamped into the owning phase's span on the way out, because the backend does
 * the same (`PlanTree`, `AnExerciseWindowNeverReachesOutsideItsPhase`) and because a stored window
 * may legitimately reach outside one: `rebasePhaseWindows` keeps an out-of-span bound rather than
 * tidying it away, so a shrink-then-grow does not lose the weeks the trainer cut. What that means
 * here is that the AUTHORED window is not the effective one and no caller may treat it as such.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { findDayPhaseWindow, phaseStartCycles, type CycleWindow } from './cycleWindow';
import type { DayDraft, PhaseDraft } from './planDraft.types';

/** A day's authored cycle lifespan — the three fields, as one value a caller can hand around. */
export interface DayCycleWindow {
  readonly activationCycle: number | null;
  readonly retirementCycle: number | null;
  readonly excludedCycles: readonly number[];
}

/** How far a gesture on one cycle reaches. The same two answers the day editor's pin question uses. */
export type DayCycleScope = 'thisCycle' | 'onward';

function windowOf(day: DayDraft): DayCycleWindow {
  return {
    activationCycle: day.activationCycle,
    retirementCycle: day.retirementCycle,
    excludedCycles: day.excludedCycles,
  };
}

/** The day and the span of the phase it sits in, or `null` when the token is not in this tree. */
function locate(
  phases: readonly PhaseDraft[],
  daySlotToken: string,
): { readonly day: DayDraft; readonly phase: CycleWindow } | null {
  const phase = findDayPhaseWindow(phases, daySlotToken);
  if (phase === null) return null;
  const day = phases
    .flatMap((one) => one.days)
    .find((candidate) => candidate.slotToken === daySlotToken);
  return day === undefined ? null : { day, phase };
}

/** The absolute cycles a window resolves to inside one phase span, ascending. */
function resolve(window: DayCycleWindow, phase: CycleWindow): number[] {
  const first = Math.max(window.activationCycle ?? phase.startCycle, phase.startCycle);
  const phaseEnd = phase.startCycle + phase.cycleCount - 1;
  const last = Math.min(window.retirementCycle ?? phaseEnd, phaseEnd);
  const holes = new Set(window.excludedCycles);
  const cycles: number[] = [];
  for (let cycle = first; cycle <= last; cycle += 1) {
    if (!holes.has(cycle)) cycles.push(cycle);
  }
  return cycles;
}

/**
 * Every absolute cycle this day is actually prescribed in, ascending — the client's own
 * `DayTemplateResponse.Cycles`, recomputed from the authored window because the draft is what the
 * trainer is editing and the server's list is what they loaded.
 *
 * Empty for a token this tree does not hold, and empty for a window that meets no cycle of its
 * phase — which is a state the backend refuses (`plan_window_empty`) and this app therefore has to
 * be able to recognise before it sends one.
 */
export function effectiveDayCycles(
  phases: readonly PhaseDraft[],
  daySlotToken: string,
): readonly number[] {
  const site = locate(phases, daySlotToken);
  return site === null ? [] : resolve(windowOf(site.day), site.phase);
}

/**
 * Which PHASE an absolute cycle falls in — its index in `phases`, and the span it covers.
 *
 * The calendar's add-day gesture is the caller: a cell knows a date and a cycle, and every window
 * decision below resolves inside a phase, so something has to make that step. It lives here rather
 * than at the call site because the walk is `phaseStartCycles`' and a second copy of it would be a
 * second answer to "where does phase 3 begin" — the coordinate bug this whole family exists to
 * prevent.
 *
 * `null` for a cycle past the plan's last, and for a plan with no phases.
 */
export function phaseAtCycle(
  phases: readonly PhaseDraft[],
  cycle: number,
): { readonly index: number; readonly window: CycleWindow } | null {
  const starts = phaseStartCycles(phases);
  for (const [index, phase] of phases.entries()) {
    const startCycle = starts[index] ?? 1;
    const cycleCount = Math.max(1, phase.cycles);
    if (cycle >= startCycle && cycle <= startCycle + cycleCount - 1) {
      return { index, window: { startCycle, cycleCount } };
    }
  }
  return null;
}

/* ── Taking a day out of a week ────────────────────────────────────────────────────────────── */

/**
 * What "remove this training" resolves to, as the operation that carries it out.
 *
 * · `excludeCycle`  — one week becomes a hole. The day keeps its ends and everything else.
 * · `retireFrom`    — the day stops after `retirementCycle`, which is the cycle BEFORE the one the
 *                     trainer acted on. The weeks before it are untouched.
 * · `removeDay`     — the window would meet no cycle at all, so the honest edit is the deletion the
 *                     trainer effectively asked for. Sending the empty window instead earns a 400 the
 *                     trainer cannot act on, and the app knows the answer already.
 * · `nothing`       — the day does not run in that cycle to begin with, or the token is unknown.
 */
export type DayCycleRemoval =
  | { readonly kind: 'excludeCycle'; readonly cycle: number }
  | { readonly kind: 'retireFrom'; readonly retirementCycle: number }
  | { readonly kind: 'removeDay' }
  | { readonly kind: 'nothing' };

/**
 * Which of the four a removal is, decided from the day's own window and its phase's span.
 *
 * `thisCycle` becomes a hole unless the hole would empty the day; `onward` becomes a retirement
 * unless the retirement would fall before the day's first effective cycle, which is the same
 * emptiness written the other way. Both empty cases answer `removeDay` rather than being sent —
 * this is the client-side half of `EnsureNoWindowIsEmpty`, taken so the trainer meets a deletion
 * they can undo instead of a refusal they cannot read.
 *
 * A cycle the day does not run in answers `nothing`, so a stale card cannot author an edit.
 */
export function planDayCycleRemoval(
  phases: readonly PhaseDraft[],
  daySlotToken: string,
  cycle: number,
  scope: DayCycleScope,
): DayCycleRemoval {
  const site = locate(phases, daySlotToken);
  if (site === null) return { kind: 'nothing' };
  const cycles = resolve(windowOf(site.day), site.phase);
  if (!cycles.includes(cycle)) return { kind: 'nothing' };

  if (scope === 'onward') {
    // Retiring at the cycle before the FIRST one the day runs in withdraws every occurrence it has,
    // which is the deletion rather than a window edit.
    return cycles[0] === cycle
      ? { kind: 'removeDay' }
      : { kind: 'retireFrom', retirementCycle: cycle - 1 };
  }
  return cycles.length === 1 ? { kind: 'removeDay' } : { kind: 'excludeCycle', cycle };
}

/* ── Putting a day back into a week ────────────────────────────────────────────────────────── */

/**
 * The window that makes this day run in `cycle` again — the `+` on a cell whose day was cut.
 *
 * `thisCycle` restores exactly that one week and leaves every other week as it was. Reaching a
 * cycle beyond the day's retirement, or before its activation, therefore has to move the END and
 * close the cycles it just swept over as HOLES: the day was not running in them and restoring one
 * week must not quietly restore five. That is the whole reason this is a window computation rather
 * than a splice on `excludedCycles`.
 *
 * `onward` restores `cycle` and every later cycle of the phase: the retirement goes, and the holes
 * above `cycle` go with it. Holes BELOW it stay — they are weeks the trainer cut deliberately and
 * this gesture says nothing about them.
 *
 * `null` for a token this tree does not hold, or for a cycle outside the phase — no window can
 * express either.
 */
export function restoredDayWindow(
  phases: readonly PhaseDraft[],
  daySlotToken: string,
  cycle: number,
  scope: DayCycleScope,
): DayCycleWindow | null {
  const site = locate(phases, daySlotToken);
  if (site === null) return null;
  const phaseEnd = site.phase.startCycle + site.phase.cycleCount - 1;
  if (cycle < site.phase.startCycle || cycle > phaseEnd) return null;

  const current = windowOf(site.day);
  const activation = Math.max(current.activationCycle ?? site.phase.startCycle, site.phase.startCycle);
  const retirement = Math.min(current.retirementCycle ?? phaseEnd, phaseEnd);
  const holes = new Set(current.excludedCycles.filter((hole) => hole !== cycle));

  // Sweeping the ends over weeks the day was not running in leaves those weeks as holes, so the one
  // week the trainer pointed at is the only one that comes back.
  if (cycle > retirement) {
    for (let between = retirement + 1; between < cycle; between += 1) holes.add(between);
  }
  if (cycle < activation) {
    for (let between = cycle + 1; between < activation; between += 1) holes.add(between);
  }

  const nextActivation = cycle < activation ? cycle : current.activationCycle;
  const nextRetirement =
    scope === 'onward' ? null : cycle > retirement ? cycle : current.retirementCycle;
  if (scope === 'onward') {
    for (const hole of [...holes]) {
      if (hole >= cycle) holes.delete(hole);
    }
  }

  return {
    activationCycle: nextActivation,
    retirementCycle: nextRetirement,
    excludedCycles: [...holes].sort((left, right) => left - right),
  };
}

/**
 * Do the two restore answers reach different weeks? The scope question is asked only where they do.
 *
 * It is a comparison rather than a rule about retirements, and that is deliberate: a day retired
 * before `cycle` is the obvious case, but a day with a hole ABOVE `cycle` differs too, and a day
 * that simply has a hole at `cycle` and nothing else to restore does not. Reading the answer off
 * the two windows means the question appears exactly where it has two honest answers, and one place
 * decides what "onward" means.
 */
export function restoreAsksScope(
  phases: readonly PhaseDraft[],
  daySlotToken: string,
  cycle: number,
): boolean {
  const site = locate(phases, daySlotToken);
  if (site === null) return false;
  const thisCycle = restoredDayWindow(phases, daySlotToken, cycle, 'thisCycle');
  const onward = restoredDayWindow(phases, daySlotToken, cycle, 'onward');
  if (thisCycle === null || onward === null) return false;
  return (
    resolve(thisCycle, site.phase).join(',') !== resolve(onward, site.phase).join(',')
  );
}

/* ── Giving a phase a day it never had ─────────────────────────────────────────────────────── */

/**
 * The window a NEW day gets from the trainer's answer about how far it reaches.
 *
 * · `thisCycle` — `{N, N}`, a training that happens once.
 * · `onward`    — open-ended from `N`, and `{null, null}` when `N` is the phase's own first cycle,
 *                 because "from the start of its phase" is what an absent activation MEANS and
 *                 pinning it to a number would freeze the day to whatever the phase spanned today.
 *
 * @param phase The span the day is being added into — its own phase, never the plan.
 */
export function addedDayWindow(
  phase: CycleWindow,
  cycle: number,
  scope: DayCycleScope,
): DayCycleWindow {
  if (scope === 'thisCycle') {
    return { activationCycle: cycle, retirementCycle: cycle, excludedCycles: [] };
  }
  return cycle <= phase.startCycle
    ? { activationCycle: null, retirementCycle: null, excludedCycles: [] }
    : { activationCycle: cycle, retirementCycle: null, excludedCycles: [] };
}

/**
 * Does adding a day at this cycle have two different answers?
 *
 * Only at the phase's LAST cycle does it not: "this cycle" and "this cycle and every one after it"
 * name the same single week, so the question would have one honest answer and asking it is noise.
 * The day then takes the explicit `{N, N}` — never the open end, which would silently widen the day
 * over the new weeks if the phase is ever lengthened.
 */
export function addAsksScope(phase: CycleWindow, cycle: number): boolean {
  return cycle < phase.startCycle + phase.cycleCount - 1;
}
